import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import { DropPoint } from '@team-milva/lab-models/resources'
import type { IDropPointLab, IDropPointPrivate } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { isLabCustomerCompanyUser } from '@team-milva/lab-models/user'

export const useDropPointStore = defineStore('dropPoint', {
  state: () => ({
    labDropPoints: [] as DropPoint[],
    unsubscribeLabDropPoints: null as (() => void) | null,

    privateDropPoints: [] as DropPoint[],
    unsubscribePrivateDropPoints: null as (() => void) | null,
  }),

  actions: {
    getDropPoints(): Promise<DropPoint[]> {
      if (this.allDropPointsLoaded) {
        return Promise.resolve(this.allDropPoints)
      }

      const userStore = useUserStore()

      if (!userStore.currentUser) {
        return Promise.resolve([])
      }

      const labAccountID = userStore.currentUser.labAccountID

      const labDropPointsPromise = new Promise<void>((resolve) => {
        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'LabDropPoints'),
          where('labAccountID', '==', labAccountID),
          where('activeState', '==', 'active')
        )
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const dropPoints: DropPoint[] = []
          querySnapshot.forEach((docSnap) => {
            dropPoints.push(new DropPoint(docSnap.data() as IDropPointLab))
          })
          this.labDropPoints = dropPoints
          this.unsubscribeLabDropPoints = unsubscribe

          if (!resolved) {
            resolved = true
            resolve()
          }
        })
      })

      const privateDropPointsPromise = new Promise<void>((resolve) => {
        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'PrivateDropPoints'),
          where('labAccountID', '==', labAccountID),
          where('activeState', '==', 'active')
        )
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const dropPoints: DropPoint[] = []
          querySnapshot.forEach((docSnap) => {
            dropPoints.push(new DropPoint(docSnap.data() as IDropPointPrivate))
          })
          this.privateDropPoints = dropPoints
          this.unsubscribePrivateDropPoints = unsubscribe

          if (!resolved) {
            resolved = true
            resolve()
          }
        })
      })

      return Promise.all([labDropPointsPromise, privateDropPointsPromise]).then(
        () => this.allDropPoints
      )
    },

    resetStore(): void {
      this.labDropPoints = []
      this.unsubscribeLabDropPoints?.()
      this.unsubscribeLabDropPoints = null

      this.privateDropPoints = []
      this.unsubscribePrivateDropPoints?.()
      this.unsubscribePrivateDropPoints = null
    },
  },

  getters: {
    allDropPointsLoaded: (state) =>
      !!state.unsubscribeLabDropPoints && !!state.unsubscribePrivateDropPoints,

    allDropPoints: (state): DropPoint[] => [
      ...state.labDropPoints,
      ...state.privateDropPoints,
    ],

    // The one lab drop point the lab has set up to receive posted samples
    // (`options.isSelfSendTarget`, at most one per lab — enforced server-side). "Jeg sender selv"
    // is only offered while this exists, and its address is the one the customer is told to send
    // to. `activeState` is checked here too, not just in the query, so a deactivated drop point
    // can never be offered whatever the listener returns.
    selfSendDropPoint: (state): DropPoint | undefined =>
      state.labDropPoints.find(
        (dropPoint) =>
          dropPoint.type === 'lab' &&
          dropPoint.activeState === 'active' &&
          dropPoint.options.isSelfSendTarget
      ),

    // Private drop points are scoped to a specific customer (an admin-configured
    // pickup arrangement) — allDropPoints returns every private drop point across
    // the whole lab account, so checkout must filter down to the current
    // customer's own before offering them as a choice.
    checkoutDropPoints: (state): DropPoint[] => {
      const userStore = useUserStore()
      // Business and insurance users alike (MIL-402) — matched on the `customer` reference.
      const customer = isLabCustomerCompanyUser(userStore.currentUser)
        ? userStore.currentUser.customer
        : undefined
      return [
        ...state.labDropPoints,
        ...state.privateDropPoints.filter(
          (x) =>
            !!customer &&
            x.customer?.type === customer.type &&
            x.customer.id === customer.id
        ),
      ]
    },

    // Guards checkout against submitting a dropPointID that isn't (or is no longer)
    // one of the real drop points the customer was shown — e.g. a restored draft
    // pointing at a drop point since deactivated. Returns true while the store
    // hasn't loaded yet, since checkoutDropPoints is then just the mock fallback
    // shown in the UI and has nothing real to validate against.
    isValidCheckoutDropPointID(): (dropPointID: string) => boolean {
      return (dropPointID: string): boolean => {
        if (!this.allDropPointsLoaded) return true
        return this.checkoutDropPoints.some((x) => x.id === dropPointID)
      }
    },
  },
})
